<template>
  <v-dialog
    v-model="dialog"
    :max-width="450"
    persistent
    :retain-focus="false"
    @keydown.esc="escape(closeDialog)"
    @click:outside="escape(closeDialog)"
    @keydown.enter="submit"
  >
    <v-card outlined>
      <v-card-title>
        {{ $t('user.inviteUser') }}
        <p-btn-icon
          type="close"
          btn-class="mr-n3 ml-auto"
          tabindex="4"
          @click="escape(closeDialog)"
        ></p-btn-icon>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-form ref="form" @submit.stop.prevent>
          <v-row>
            <v-col>
              <v-combobox
                v-model="contact"
                :items="contactsSuggestions"
                :label="$t('user.invite.email')"
                :rules="rules.contact"
                item-text="searchText"
                append-icon=""
                tabindex="1"
                clearable
                validate-on-blur
                autofocus
                hide-selected
              >
                <template v-slot:selection="{ item }">
                  {{ typeof (item) === 'string' ? item : item.email }}
                </template>
                <template v-slot:item="{ item }">
                  <p-avatar
                    :color="item.color"
                    :email="item.email"
                    :char="item.displayName[0]"
                    list-item-avatar
                  ></p-avatar>
                  <v-list-item-content>
                    <v-list-item-title v-text="item.displayNameWithCompany"></v-list-item-title>
                    <v-list-item-subtitle v-text="item.email"></v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-combobox>

              <v-select
                v-model="roleId"
                :items="roles"
                :label="$t('user.invite.role')"
                :rules="rules.roleId"
                item-value="id"
                item-text="name"
                tabindex="1"
              ></v-select>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="justify-center">
        <v-btn text tabindex="3" @click="closeDialog">{{ $t('cancel') }}</v-btn>
        <v-btn text :disabled="!isModelChanged()" color="primary" tabindex="2" @click="submit">{{ $t('user.invite.sendInvitation') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>


<script>
import { mapState, mapGetters, mapActions } from 'vuex'

import PAvatar from '@/components/PAvatar'
import PBtnIcon from '@/components/PBtnIcon'

import snackbarMixin from '@/mixins/snackbar.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'


export default {
  components: {
    PAvatar,
    PBtnIcon
  },


  mixins: [
    snackbarMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin
  ],


  data () {
    return {
      contact: null,
      roleId: null,
      rule: {
        inviteEmail: value => {
          if (value === null) return true
          if (typeof value === 'object') {
            return this.rule.email()(value.email)
          }
          return this.rule.email()(value)
        }
      }
    }
  },


  computed: {
    ...mapState('contact', ['emailTypes']),
    ...mapGetters('contact', ['shortContacts']),
    ...mapGetters('role', ['roles']),

    contactsSuggestions () {
      return this.shortContacts
        .filter(contact => !!contact.email)
        .map(contact => {
          return {
            ...contact,
            searchText: `${contact.displayNameWithCompany} ${contact.email}`
          }
        })
    },

    rulesToApply () {
      const rule = this.rule

      return {
        contact: [
          rule.required(),
          rule.inviteEmail
        ],
        roleId: [rule.required()]
      }
    }
  },


  methods: {
    ...mapActions('contact', ['loadShortContacts', 'createContact', 'loadEmailTypes']),
    ...mapActions('user', ['inviteUser']),

    onDialogOpen () {
      this.contact = null
      this.roleId = this.roles.length > 0 ? this.roles[0].id : null
      this.initWatchedModel(this.contact)
      this.resetValidation()
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        this.showSnackbarProcessing()

        const data = {
          roleId: this.roleId
        }

        if (typeof (this.contact) === 'string') {
          const email = this.contact
          let contact = {
            firstName: email,
            email: {
              email: email,
              isPrimary: true,
              emailType: this.emailTypes[0]
            },
            isActive: true
          }
          contact = await this.createContact(contact)
          data.contactId = contact.id
          data.emailAddress = email
        } else {
          data.contactId = this.contact.id
          data.emailAddress = this.contact.email
        }

        await this.inviteUser(data)
        this.showSnackbarSuccess(this.$t('user.invite.sent'))

        this.closeDialog()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  },


  created () {
    try {
      this.loadShortContacts()
      this.loadEmailTypes()
    } catch (error) {
      this.$log.error(error)
    }
  }
}
</script>
